import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

/** Cabinet-oblique helper: projects a 3D point to 2D SVG coords.
 *  origin is the SVG point for (0,0,0). */
function proj(x: number, y: number, z: number, ox = 140, oy = 95): [number, number] {
  // Cabinet oblique: X right, Z up, Y recedes at 45° with 0.5 foreshortening
  return [ox + x - y * 0.4, oy - z + y * 0.35];
}

export function CubeDiagram({ values }: DiagramProps) {
  const rawSize = values.size ?? [10, 10, 10];
  const centered = values.center ?? true;

  // Normalize displayed dimensions proportionally, max 80px
  const sx = Array.isArray(rawSize) ? rawSize[0] : 10;
  const sy = Array.isArray(rawSize) ? rawSize[1] : 10;
  const sz = Array.isArray(rawSize) ? rawSize[2] : 10;
  const maxVal = Math.max(sx, sy, sz, 1);
  const scale = 70 / maxVal;
  const dx = sx * scale;
  const dy = sy * scale;
  const dz = sz * scale;

  // 8 corners of the box in 3D (when centered=false, base at z=0)
  const zOff = centered ? -dz / 2 : 0;

  const corners = [
    proj(0,   0,   zOff,       140, 95),   // 0: front-left-bottom
    proj(dx,  0,   zOff,       140, 95),   // 1: front-right-bottom
    proj(dx,  dy,  zOff,       140, 95),   // 2: back-right-bottom
    proj(0,   dy,  zOff,       140, 95),   // 3: back-left-bottom
    proj(0,   0,   zOff+dz,    140, 95),   // 4: front-left-top
    proj(dx,  0,   zOff+dz,    140, 95),   // 5: front-right-top
    proj(dx,  dy,  zOff+dz,    140, 95),   // 6: back-right-top
    proj(0,   dy,  zOff+dz,    140, 95),   // 7: back-left-top
  ];

  const [c0, c1, c2, c3, c4, c5, c6, c7] = corners;

  // Visible faces: front (0,1,5,4), right (1,2,6,5), top (4,5,6,7)
  const face = (pts: [number, number][]) =>
    pts.map(([x, y]) => `${x},${y}`).join(' ');

  // Dimension arrow helpers
  const arrowLen = 7;
  function arrowhead(x: number, y: number, angle: number) {
    const r = (Math.PI / 180) * angle;
    const p1x = x + arrowLen * Math.cos(r + 0.5);
    const p1y = y + arrowLen * Math.sin(r + 0.5);
    const p2x = x + arrowLen * Math.cos(r - 0.5);
    const p2y = y + arrowLen * Math.sin(r - 0.5);
    return `M${p1x},${p1y} L${x},${y} L${p2x},${p2y}`;
  }

  // Origin marker
  const originPt = centered
    ? proj(dx / 2, dy / 2, zOff + dz / 2, 140, 95)
    : c0;

  // X dimension: bottom front edge (c0 → c1), shown below
  const xMid: [number, number] = [(c0[0] + c1[0]) / 2, (c0[1] + c1[1]) / 2 + 10];
  const angleX = Math.atan2(c1[1] - c0[1], c1[0] - c0[0]) * 180 / Math.PI;

  // Z dimension: right side (c1 → c5), shown to the right
  const zMid: [number, number] = [(c1[0] + c5[0]) / 2 + 12, (c1[1] + c5[1]) / 2];
  const angleZ = Math.atan2(c5[1] - c1[1], c5[0] - c1[0]) * 180 / Math.PI;

  // Y dimension: receding edge (c1 → c2), shown above-right
  const yMid: [number, number] = [(c1[0] + c2[0]) / 2 + 8, (c1[1] + c2[1]) / 2 - 6];

  return (
    <svg
      viewBox="0 0 280 150"
      width="100%"
      style={{ display: 'block' }}
      aria-label="Cube diagram"
    >
      <defs>
        <marker id="arr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.DIM_STROKE} />
        </marker>
        <marker id="arr-rev" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
          <path d="M5,1 L0,3 L5,5 Z" fill={D.DIM_STROKE} />
        </marker>
      </defs>

      {/* Hidden/back edges */}
      <line x1={c0[0]} y1={c0[1]} x2={c3[0]} y2={c3[1]} stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
      <line x1={c3[0]} y1={c3[1]} x2={c7[0]} y2={c7[1]} stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
      <line x1={c3[0]} y1={c3[1]} x2={c2[0]} y2={c2[1]} stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />

      {/* Visible faces */}
      <polygon points={face([c0, c1, c5, c4])} fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <polygon points={face([c1, c2, c6, c5])} fill="rgba(96,165,250,0.04)" stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <polygon points={face([c4, c5, c6, c7])} fill="rgba(96,165,250,0.1)" stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* X dimension */}
      <line x1={c0[0]} y1={c0[1] + 9} x2={c1[0]} y2={c1[1] + 9}
            stroke={D.X_COLOR} strokeWidth="1" strokeDasharray={D.DIM_DASH}
            markerEnd="url(#arr)" markerStart="url(#arr-rev)" />
      <text x={xMid[0]} y={xMid[1] + 10} fontSize="10" fontFamily="monospace"
            fill={D.X_COLOR} textAnchor="middle" opacity="0.9">X</text>

      {/* Z dimension */}
      <line x1={c1[0] + 9} y1={c1[1]} x2={c5[0] + 9} y2={c5[1]}
            stroke={D.Z_COLOR} strokeWidth="1" strokeDasharray={D.DIM_DASH}
            markerEnd="url(#arr)" markerStart="url(#arr-rev)" />
      <text x={zMid[0] + 8} y={zMid[1]} fontSize="10" fontFamily="monospace"
            fill={D.Z_COLOR} textAnchor="start" opacity="0.9">Z</text>

      {/* Y dimension */}
      <line x1={c1[0]} y1={c1[1]} x2={c2[0]} y2={c2[1]}
            stroke={D.Y_COLOR} strokeWidth="1" strokeDasharray={D.DIM_DASH}
            markerEnd="url(#arr)" />
      <text x={yMid[0]} y={yMid[1]} fontSize="10" fontFamily="monospace"
            fill={D.Y_COLOR} textAnchor="start" opacity="0.9">Y</text>

      {/* Origin cross */}
      <line x1={originPt[0] - 4} y1={originPt[1]} x2={originPt[0] + 4} y2={originPt[1]}
            stroke={D.ORIGIN_COLOR} strokeWidth="1" />
      <line x1={originPt[0]} y1={originPt[1] - 4} x2={originPt[0]} y2={originPt[1] + 4}
            stroke={D.ORIGIN_COLOR} strokeWidth="1" />

      {/* Center label */}
      <text x="8" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45">
        {centered ? 'center=true' : 'center=false'}
      </text>

      {/* Size values */}
      <text x="272" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45" textAnchor="end">
        [{sx}, {sy}, {sz}]
      </text>
    </svg>
  );
}
